/**
 * Demat & CDSL Depository Helpers for FCML (Federal Capital Markets Limited)
 */

export const FCML_DP_ID = "12088000";
export const FCML_DP_NAME = "Federal Capital Markets Limited";
export const DEPOSITORY_NAME = "CDSL";

/**
 * Formulates the standard 16-digit CDSL Demat Account Number for FCML.
 * Formula: 8-digit DP ID (12088000) + 8-digit zero-padded Client/Beneficiary ID.
 *
 * @param {string|number} clientId - The 8-digit or variable length client ID / trdr_id
 * @returns {string} 16-digit CDSL Demat Account Number
 */
export function calculateDematAccount(clientId) {
  if (!clientId) return "";
  const cleaned = String(clientId).trim().replace(/\D/g, "");
  if (!cleaned) return "";

  // If already 16 digits and starts with 12088000, return as-is
  if (cleaned.length === 16 && cleaned.startsWith(FCML_DP_ID)) {
    return cleaned;
  }

  // If already 16 digits with another DP ID, return as-is
  if (cleaned.length === 16) {
    return cleaned;
  }

  // If 8 digits or less, pad to 8 digits with leading zeros
  const paddedClientId = cleaned.slice(-8).padStart(8, "0");
  return `${FCML_DP_ID}${paddedClientId}`;
}

/**
 * Validates whether an account number is a valid 16-digit numeric Demat number.
 * @param {string} demat - Account number to validate
 * @returns {boolean}
 */
export function isValidDemat(demat) {
  if (!demat) return false;
  const cleaned = String(demat).trim().replace(/\s+/g, "");
  return /^\d{16}$/.test(cleaned);
}

/**
 * Formats a 16-digit Demat number into human-readable spaced blocks: `12088000 00012345`
 * @param {string} demat
 * @returns {string}
 */
export function formatDematDisplay(demat) {
  if (!demat) return "-";
  const cleaned = String(demat).trim().replace(/\s+/g, "");
  if (cleaned.length === 16) {
    return `${cleaned.slice(0, 8)} ${cleaned.slice(8)}`;
  }
  return demat;
}
